import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Typography,
  TextField,
  FormControl,
  useMediaQuery,
  MenuItem,
  CircularProgress,
} from '@mui/material';

import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';

const TABLE_HEAD = [
  { id: 'usageDate', label: 'Kullanım Tarihi', alignRight: false },
  { id: 'requestId', label: 'Sipariş ID / İşlem ID', alignRight: false },
  { id: 'usageTime', label: 'Saat', alignRight: false },
  { id: 'campaignName', label: 'Kampanya Adı', alignRight: false },
  { id: 'couponCode', label: 'Kupon Kodu', alignRight: false },
  { id: 'customerIdPhone', label: 'Müşteri ID / Telefon', alignRight: false },
  { id: 'customerName', label: 'Müşteri İsmi', alignRight: false },
  { id: 'isFirstUse', label: 'Servis İlk Kullanım', alignRight: false },
  { id: 'couponFirstUse', label: 'Kupon İlk Kullanım', alignRight: false },
  { id: 'transactionAmount', label: 'İşlem Tutarı', alignRight: false },
  { id: 'discountAmount', label: 'İndirim Tutarı', alignRight: false },
  { id: 'netAmount', label: 'Net Tutar', alignRight: false },
  { id: 'location', label: 'Lokasyon (AVM)', alignRight: false },
  { id: 'service', label: 'Hizmet', alignRight: false },
  { id: 'completedDateTime', label: 'Tamamlanma Zamanı', alignRight: false },
  { id: 'transactionStatus', label: 'İşlem Statüsü', alignRight: false },
];

const serviceLabels = {
  'pet-car': 'Evcil Hayvan Aracı',
  'kid-car': 'Çocuk Arabası',
  gift: 'Hediye Paketi',
  all: 'Tümü',
};

const formatTs = (ts) => {
  if (!ts) return null;
  const d = new Date(Number(ts) * 1000);
  return d;
};

const tsToDate = (ts) => {
  const d = formatTs(ts);
  if (!d) return '-';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const tsToTime = (ts) => {
  const d = formatTs(ts);
  if (!d) return '-';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const joinName = (name, lastname) => {
  const fullName = `${name || ''} ${lastname || ''}`.trim();
  return fullName || '-';
};

const formatOptionalAmount = (value, currency = 'TL') => {
  if (value === undefined || value === null || value === '') return '-';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return String(value);
  return `${asNumber} ${currency}`;
};

const boolToLabel = (value) => {
  if (value === true || value === 1 || value === '1') return 'Evet';
  if (value === false || value === 0 || value === '0') return 'Hayır';
  return '-';
};

const mapRow = (it, idx, mallsMap) => ({
  no: idx + 1,
  usageDate: tsToDate(it.usedDateTime),
  usageTime: tsToTime(it.usedDateTime),
  campaignName: it.title || '-',
  couponCode: it.couponCode || '-',
  customerIdPhone: it.usedBy || it.used_by || '-',
  customerName: joinName(it?.usedByInfo?.name, it?.usedByInfo?.lastname),
  isFirstUse: boolToLabel(
    it.isServiceFirstUse ??
      it.isServiceUsedFirst ??
      it?.usedByInfo?.isServiceFirstUse ??
      it?.usedByInfo?.isServiceUsedFirst ??
      it.isFirstUse ??
      it?.usedByInfo?.isFirstUse ??
      it.firstUse ??
      it.is_first_use
  ),
  couponFirstUse: boolToLabel(it?.usedByInfo?.isFirstUse),
  transactionAmount: formatOptionalAmount(
    it.transactionAmount ??
      it?.usedByInfo?.transactionAmount ??
      it.amount ??
      it.price ??
      it?.usedByInfo?.amount,
    it.currency || it?.usedByInfo?.currency || 'TL'
  ),
  discountAmount:
    it.discountAmount !== undefined && it.discountAmount !== null
      ? formatOptionalAmount(
          it.discountAmount,
          it.currency || it?.usedByInfo?.currency || 'TL'
        )
      : it.discountValue
      ? it.discountType === 'percentage'
        ? `%${it.discountValue}`
        : `${it.discountValue} TL`
      : '-',
  netAmount: formatOptionalAmount(
    it.netAmount ??
      it?.usedByInfo?.netAmount ??
      it.finalAmount ??
      it.paidAmount,
    it.currency || it?.usedByInfo?.currency || 'TL'
  ),
  location: it?.usedByInfo?.mallTitle || mallsMap[it.mallID] || it.mallID || '-',
  service: serviceLabels[it.discount_for_product] || it.discount_for_product || '-',
  requestId: it?.usedByInfo?.requestID || it.requestID || '-',
  completedDateTime: it.completedDateTime
    ? `${tsToDate(it.completedDateTime)} ${tsToTime(it.completedDateTime)}`
    : '-',
  transactionStatus: it.completedDateTime ? 'Tamamlandı' : 'Tamamlanmadı',
});

export default function CouponsPage() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const isMobile = useMediaQuery('(max-width:768px)');

  const [order] = useState('asc');
  const [orderBy] = useState('no');
  const [filterName] = useState('');
  const [showPopUpError, setShowPopUpError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [originalData, setOriginalData] = useState([]);
  const [couponList, setCouponList] = useState([]);
  const [malls, setMalls] = useState([]);

  const [filterModal, setFilterModal] = useState(false);
  const handleModal = () => setFilterModal(true);
  const handleCloseModal = () => setFilterModal(false);

  const [selectedMall, setSelectedMall] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchCoupons = async (mallIDs, mallsMap) => {
    setLoading(true);
    try {
      const res = await Services.GetEasydeskCouponList(parsedUser.token, mallIDs);
      const list = res.data.result || [];
      const mapped = list.map((it, idx) => mapRow(it, idx, mallsMap));
      setOriginalData(mapped);
      setCouponList(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching coupons', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const res = await Services.GetMalls(parsedUser.token);
        const mallsList = res.data.result || [];
        setMalls(mallsList);

        const mallsMap = {};
        mallsList.forEach((m) => {
          mallsMap[m.id || m._id] = m.title;
        });

        const allIDs = mallsList.map((m) => String(m.id || m._id));
        await fetchCoupons(allIDs, mallsMap);
      } catch (err) {
        console.error('Error during init', err);
      }
    };
    init();
  }, []);

  const buildMallsMap = () => {
    const map = {};
    malls.forEach((m) => { map[m.id || m._id] = m.title; });
    return map;
  };

  const applyFilters = async () => {
    const mallsMap = buildMallsMap();
    const mallIDs = selectedMall
      ? [selectedMall]
      : malls.map((m) => String(m.id || m._id));
    const data = await fetchCoupons(mallIDs, mallsMap);
    const filtered = data.filter((item) =>
      statusFilter ? item.transactionStatus === statusFilter : true
    );
    setCouponList(filtered);
    handleCloseModal();
  };

  const clearFilters = async () => {
    setSelectedMall('');
    setStatusFilter('');
    const mallsMap = buildMallsMap();
    const allIDs = malls.map((m) => String(m.id || m._id));
    handleCloseModal();
    await fetchCoupons(allIDs, mallsMap);
  };

  function exportToExcel() {
    const temp = couponList.map((it) => ({
      'Kullanım Tarihi': it.usageDate,
      Saat: it.usageTime,
      'Sipariş ID / İşlem ID': it.requestId,
      'Kampanya Adı': it.campaignName,
      'Kupon Kodu': it.couponCode,
      'Müşteri ID / Telefon': it.customerIdPhone,
      'Müşteri İsmi': it.customerName,
      'Servis İlk Kullanım': it.isFirstUse,
      'Kupon İlk Kullanım': it.couponFirstUse,
      'İşlem Tutarı': it.transactionAmount,
      'İndirim Tutarı': it.discountAmount,
      'Net Tutar': it.netAmount,
      'Lokasyon (AVM)': it.location,
      Hizmet: it.service,
      'Tamamlanma Zamanı': it.completedDateTime,
      'İşlem Statüsü': it.transactionStatus,
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: temp,
        header: {
          'Kullanım Tarihi': 'string',
          Saat: 'string',
          'Sipariş ID / İşlem ID': 'string',
          'Kampanya Adı': 'string',
          'Kupon Kodu': 'string',
          'Müşteri ID / Telefon': 'string',
          'Müşteri İsmi': 'string',
          'Servis İlk Kullanım': 'string',
          'Kupon İlk Kullanım': 'string',
          'İşlem Tutarı': 'string',
          'İndirim Tutarı': 'string',
          'Net Tutar': 'string',
          'Lokasyon (AVM)': 'string',
          Hizmet: 'string',
          'Tamamlanma Zamanı': 'string',
          'İşlem Statüsü': 'string',
        },
        sheetTitle: 'Kuponlar',
      })
      .then((response) => {
        if (response.data.status) {
          const w = window.open(response.data.result, '_blank');
          if (!w || w.closed || typeof w.closed === 'undefined') {
            setShowPopUpError(true);
          }
        }
      })
      .catch(() => setShowPopUpError(true));
  }

  function descendingComparator(a, b, key) {
    if (b[key] < a[key]) return -1;
    if (b[key] > a[key]) return 1;
    return 0;
  }
  function getComparator(ord, key) {
    return ord === 'desc'
      ? (a, b) => descendingComparator(a, b, key)
      : (a, b) => -descendingComparator(a, b, key);
  }
  function applySortFilter(arr, cmp, query) {
    const stabilized = arr.map((el, i) => [el, i]);
    stabilized.sort((a, b) => {
      const comp = cmp(a[0], b[0]);
      if (comp !== 0) return comp;
      return a[1] - b[1];
    });
    const sorted = stabilized.map((el) => el[0]);
    if (query) {
      return filter(
        sorted,
        (row) =>
          row.campaignName.toLowerCase().includes(query.toLowerCase()) ||
          row.couponCode.toLowerCase().includes(query.toLowerCase())
      );
    }
    return sorted;
  }

  const displayList = applySortFilter(couponList, getComparator(order, orderBy), filterName);

  const uniqueStatuses = [...new Set(originalData.map((d) => d.transactionStatus).filter(Boolean))];

  return (
    <>
      <Helmet>
        <title>Kuponlar</title>
      </Helmet>

      <Stack sx={{ px: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Kuponlar</Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          <ExportToExcelButton
            showPopUpError={showPopUpError}
            setShowPopUpError={setShowPopUpError}
            onClick={exportToExcel}
          />
          {isMobile && <FilterViewButton onClick={handleModal} />}
        </Stack>

        <Card>
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={handleCloseModal}
          >
            <Stack spacing={2} p={2}>
              <FormControl fullWidth>
                <TextField
                  select
                  label="AVM"
                  value={selectedMall}
                  onChange={(e) => setSelectedMall(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {malls.map((mall) => (
                    <MenuItem key={mall.id || mall._id} value={String(mall.id || mall._id)}>
                      {mall.title}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  select
                  label="İşlem Statüsü"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {uniqueStatuses.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <ClearButton onClick={clearFilters} />
                <FilterButton onClick={applyFilters} />
              </Stack>
            </Stack>
          </CustomFilter>

          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <DynamicTable
              data={displayList}
              headers={TABLE_HEAD}
              initialOrder="no"
              initalOrderDirection="asc"
              isOrdered
              isMobile={isMobile}
              handleModal={handleModal}
              handleCloseModal={handleCloseModal}
            />
          )}
        </Card>
      </Stack>
    </>
  );
}
